import React, { type PropsWithChildren, useRef } from 'react';
import { StyleSheet, TouchableOpacity, type TouchableOpacityProps, View } from 'react-native';

import { theme } from '@garnish/constants';

import { useHovered } from '../../../../../../hooks';
import { BodyText } from '../../../../../Text';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarFloatingTextLink = (props: Props) => {
  const { children, emphasized = false, onPress, ...restProps } = props;

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const linkRef = useRef<View>(null);
  const isHovered = useHovered(linkRef);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const labelStyles = [styles.label, emphasized ? styles.emphasizedLabel : undefined];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity accessibilityRole="link" activeOpacity={0.7} onPress={onPress} {...restProps}>
      <View ref={linkRef} style={styles.link}>
        <BodyText
          bold={emphasized}
          maxFontSizeMultiplier={1.2}
          underline={isHovered}
          sizeMatch={['16']}
          style={labelStyles}
        >
          {children}
        </BodyText>
      </View>
    </TouchableOpacity>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  link: {
    justifyContent: 'center',
    borderRadius: theme.radius.medium,
    paddingVertical: theme.spacing['2'],
  },
  hoverOverlay: {
    position: 'absolute',
    top: 0,
    right: -theme.spacing['1'],
    bottom: 0,
    left: -theme.spacing['1'],
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.WHITE,
  },
  label: {
    color: theme.colors.CHARCOAL,
  },
  emphasizedLabel: {
    color: theme.colors.KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<
  Pick<TouchableOpacityProps, 'onPress'> & {
    emphasized?: boolean;
  }
>;
